import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { WORKING_COPY_ID } from '@/components/capability-detail/CapabilityDetailLayout';
import { useCapabilityDetail as useCapabilityDetailData, useRevisionFiles } from '@/hooks/use-entity-detail';
import { api } from '@/lib/api-client';
import { logger } from '@/lib/telemetry';
import { toast } from 'sonner';
import { parseManifest, normalizeNewlines, extractFilesAndManifest } from '@/lib/revision-file-utils';
import type { CapabilityRevisionFile } from '@/types';

export interface FileResolverArgs {
    files: CapabilityRevisionFile[];
    typeConfig: Record<string, unknown> | null | undefined;
}

export interface UseFileRevisionEditorOptions {
    /**
     * Resolve the primary editable file from the available files and type config.
     * Must be a stable reference (module-level function or wrapped in useCallback).
     */
    resolveFilePath: (args: FileResolverArgs) => string;
    /**
     * Content type for the primary file when saving.
     * Reactive — the latest value is captured via ref at save time.
     * @default 'text/plain'
     */
    primaryContentType?: string;
    /**
     * Override file path when creating a brand-new file (no existing working files).
     * Useful when the path depends on user-selected language (e.g. `handler.ts` vs `handler.py`).
     * When omitted, the resolved working file path is used.
     */
    newFilePath?: string;
    logPrefix?: string;
    saveSuccessMessage?: string;
    saveErrorMessage?: string;
    /** Override useParams().appKey (for embedded/tiled context). */
    appKey?: string;
    /** Override useParams().name (for embedded/tiled context). */
    capabilityName?: string;
}

export function useFileRevisionEditor({
    resolveFilePath,
    primaryContentType = 'text/plain',
    newFilePath,
    logPrefix = 'file-revision-editor',
    saveSuccessMessage = 'File saved',
    saveErrorMessage = 'Failed to save file',
    appKey: appKeyOpt,
    capabilityName,
}: UseFileRevisionEditorOptions) {
    const params = useParams();
    const appKey = appKeyOpt ?? params.appKey;
    const name = capabilityName ?? params.name;
    const queryClient = useQueryClient();
    const { data, isLoading } = useCapabilityDetailData(appKey!, name!);

    const [selectedRevisionId, setSelectedRevisionId] = useState(WORKING_COPY_ID);
    const isViewingPublished = selectedRevisionId !== WORKING_COPY_ID;

    const immutableRevisions = useMemo(
        () => ((data as Record<string, unknown> | undefined)?.immutable_revisions ?? []) as Array<{
            id: string; files?: CapabilityRevisionFile[]; type_config?: unknown;
        }>,
        [data],
    );

    const selectedImmutableRevision = useMemo(
        () => immutableRevisions.find((r) => r.id === selectedRevisionId),
        [immutableRevisions, selectedRevisionId],
    );

    const {
        data: revisionFilesData,
        isLoading: isLoadingRevisionFiles,
    } = useRevisionFiles(
        appKey ?? '',
        name ?? '',
        isViewingPublished ? selectedRevisionId : '',
    );

    // --- Working copy data ---

    const { files: workingFiles, manifest: workingManifest } = useMemo(
        () =>
            data
                ? extractFilesAndManifest(data as Record<string, unknown>)
                : { files: [] as CapabilityRevisionFile[], manifest: null },
        [data],
    );

    // --- Active (displayed) files based on selected revision ---

    const activeFiles: CapabilityRevisionFile[] = useMemo(() => {
        if (!isViewingPublished) return workingFiles;
        if (revisionFilesData) {
            const raw = revisionFilesData as
                | CapabilityRevisionFile[]
                | { files?: CapabilityRevisionFile[]; data?: CapabilityRevisionFile[] };
            if (Array.isArray(raw)) return raw;
            return raw.data ?? raw.files ?? [];
        }
        return selectedImmutableRevision?.files ?? [];
    }, [isViewingPublished, workingFiles, revisionFilesData, selectedImmutableRevision]);

    const activeManifest = useMemo(() => {
        if (!isViewingPublished) return workingManifest;
        return selectedImmutableRevision ? parseManifest(selectedImmutableRevision.type_config) : null;
    }, [isViewingPublished, workingManifest, selectedImmutableRevision]);

    // --- File path resolution ---

    const primaryFilePath = useMemo(
        () => resolveFilePath({ files: activeFiles, typeConfig: activeManifest }),
        [activeFiles, activeManifest, resolveFilePath],
    );

    const serverContent = useMemo(() => {
        const f = activeFiles.find((x) => x.path === primaryFilePath);
        return f?.content_text ?? '';
    }, [activeFiles, primaryFilePath]);

    const workingFilePath = useMemo(
        () => resolveFilePath({ files: workingFiles, typeConfig: workingManifest }),
        [workingFiles, workingManifest, resolveFilePath],
    );

    const workingServerContent = useMemo(() => {
        const f = workingFiles.find((x) => x.path === workingFilePath);
        return f?.content_text ?? '';
    }, [workingFiles, workingFilePath]);

    // --- Draft state (synced to working copy, editable by user) ---

    const [draft, setDraft] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!isViewingPublished) {
            setDraft(workingServerContent);
        }
    }, [workingServerContent, isViewingPublished]);

    const isDirty =
        !isViewingPublished &&
        normalizeNewlines(draft) !== normalizeNewlines(workingServerContent);

    // --- Capability flags ---

    const isArchived =
        ((data as { status?: string } | undefined)?.status ?? '').toLowerCase() === 'archived';
    const hasWorkingRevision =
        (data as Record<string, unknown> | undefined)?.working_revision != null;

    // --- Display helpers ---

    const displayedValue = isViewingPublished ? serverContent : draft;
    const displayedLoading = isViewingPublished ? isLoadingRevisionFiles : isLoading;

    // --- Save logic ---

    const primaryContentTypeRef = useRef(primaryContentType);
    primaryContentTypeRef.current = primaryContentType;

    const effectiveNewFilePath = useMemo(() => {
        if (workingFiles.some((f) => f.path === workingFilePath)) {
            return workingFilePath;
        }
        return newFilePath ?? workingFilePath;
    }, [workingFiles, workingFilePath, newFilePath]);

    const buildFilePayload = useCallback(() => {
        const contentType = primaryContentTypeRef.current ?? 'text/plain';
        if (workingFiles.length === 0) {
            return [{ path: effectiveNewFilePath, content: draft, contentType }];
        }
        const payload = workingFiles.map((f) => ({
            path: f.path,
            content: f.path === workingFilePath ? draft : (f.content_text ?? ''),
            contentType: f.path === workingFilePath ? contentType : (f.content_type || 'text/plain'),
        }));
        if (!workingFiles.some((f) => f.path === workingFilePath)) {
            payload.push({ path: effectiveNewFilePath, content: draft, contentType });
        }
        return payload;
    }, [workingFiles, workingFilePath, effectiveNewFilePath, draft]);

    const save = useCallback(async () => {
        if (!appKey || !name || !isDirty) return;
        setIsSaving(true);
        try {
            await api.post(
                `/applications/${appKey}/capabilities/${encodeURIComponent(name)}/upsert`,
                { targetRevision: 'working', files: buildFilePayload() },
            );
            await queryClient.invalidateQueries({
                queryKey: ['capability', appKey, name],
            });
            toast.success(saveSuccessMessage);
        } catch (err) {
            logger.error(`${logPrefix}.save: failed`, { error: err });
            toast.error(saveErrorMessage, {
                description: 'Could not save changes. Try again.',
            });
        } finally {
            setIsSaving(false);
        }
    }, [appKey, name, isDirty, buildFilePayload, queryClient, saveSuccessMessage, saveErrorMessage, logPrefix]);

    const saveRef = useRef(save);
    saveRef.current = save;
    const stableSave = useCallback(async () => saveRef.current(), []);

    const supplementalDeferredSave = useMemo(
        () => ({
            isDirty,
            isSaving,
            save: stableSave,
        }),
        [isDirty, isSaving, stableSave],
    );

    return {
        selectedRevisionId,
        setSelectedRevisionId,
        isViewingPublished,

        data,
        isLoading,

        activeFiles,
        activeManifest,
        primaryFilePath,

        workingFiles,
        workingManifest,
        workingFilePath,

        draft,
        setDraft,
        displayedValue,
        displayedLoading,

        isDirty,
        isSaving,
        supplementalDeferredSave,

        isArchived,
        hasWorkingRevision,
    };
}
